<template>
  <HeadTitle
    title="Crear flujo de aprobación"
    description="Formulario de creación de un nuevo flujo de aprobación en donde se define la secuencia de aprobación de las solicitudes de vacaciones y licencias"
  />
  <br />
  <Step :step-number="1" title="Alcance del flujo de aprobación">
    <div class="flex flex-column">
      <Dropdown
        v-model="selectedEntity"
        option-label="name"
        :options="entities"
        class="w-8 md:w-14rem"
      >
      </Dropdown>
      <div
        v-if="selectedEntity.code === 'E'"
        class="flex flex-column md:w-full mt-3"
      >
        <MultiSelectDialog
          header-title="Seleccionar empleados"
          label="Seleccionar empleados"
          :options="employees"
          @update:visible="updateDialogVisibility"
          :disabled="isDisabled"
          show-key="name"
          value-key="id"
          :required="true"
          v-model="selectedEmployees"
        />
        <small id="text-error" class="p-error">{{
          employeesError || '&nbsp;'
        }}</small>
      </div>
      <div
        v-if="selectedEntity.code === 'N'"
        class="flex flex-column md:w-full mt-3"
      >
        <MultiSelectDialog
          header-title="Seleccionar nóminas"
          label="Seleccionar nóminas"
          @update:visible="updateDialogVisibility"
          :options="payrolls"
          v-model="selectedPayrolls"
          :disabled="isDisabled"
          show-key="name"
          value-key="id"
          :required="true"
        />
        <small id="text-error" class="p-error">{{
          payrollsError || '&nbsp;'
        }}</small>
      </div>
    </div>
  </Step>
  <Divider />
  <Step :step-number="2" title="Pasos de aprobación">
    <DataTable
      v-model:editingRows="editingRows"
      :value="approvalSteps.data"
      editMode="row"
      dataKey="stepNumber"
      @row-edit-save="onRowEditSave"
      tableClass="editable-cells-table"
      tableStyle="min-width: 30rem"
      scrollHeight="600px"
      scrollable
      class="tabla"
    >
      <Column header="#" style="width: 15%" filterField="supervisor">
        <template #body="{ data, index }">
          <Chip
            class="flex justify-content-center w-5 h-2rem"
            :class="{ 'bg-teal-200': data.approver?.name !== '' }"
            :label="String(index + 1)"
          />
        </template>
      </Column>
      <Column field="approver" header="Aprobador" style="width: 30%">
        <template #body="{ data, field }">
          <span v-if="data[field].name">
            {{ data[field].name }}
          </span>
          <span v-else>
            <label class="text-600 font-medium">Sin asignar</label>
          </span>
        </template>
        <template #editor="{ data, field }">
          <Dropdown
            v-model="data[field]"
            :options="approvers"
            optionLabel="name"
            :placeholder="
              data[field] ? data[field].name : 'Selecciona un aprobador'
            "
            filter
          >
            <template #option="slotProps">
              {{ slotProps.option.name }}
            </template>
          </Dropdown>
        </template>
      </Column>
      <Column field="isBackup" header="Backup" style="width: 20%">
        <template #editor="{ data, field }">
          <Checkbox v-model="data[field]" :binary="true" />
        </template>
        <template #body="{ data, field }">
          {{ data[field] ? 'Si' : 'No' }}
        </template>
      </Column>
      <Column field="weight" header="Prioridad" style="max-width: 5%">
        <template #body="{ data, field }">
          <span class="text-green-800">
            {{ data[field] }}
          </span>
        </template>
        <template #editor="{ data, field }">
          <InputNumber
            class="w-2"
            v-model="data[field]"
            inputId="minmax"
            :min="0"
            :max="100"
            :disabled="data.isBackup"
          />
        </template>
      </Column>
      <Column field="description" header="Descripción">
        <template #body="{ data, field }">
          {{ data[field] }}
        </template>
        <template #editor="{ data, field }">
          <InputText v-model="data[field]"></InputText>
        </template>
      </Column>
      <Column
        :rowEditor="true"
        style="width: 10%; min-width: 8rem"
        bodyStyle="text-align:center"
      ></Column>
      <Column>
        <template #body="{ data, index }">
          <div class="actions">
            <Button
              class="action-btn view p-button-sm"
              icon="pi pi-trash"
              @click="
                deleteApprovalStep(
                  $event,
                  data['stepNumber'],
                  data.approver.id,
                  data.approver.name
                )
              "
              severity="danger"
              v-tooltip.bottom="'Eliminar'"
              :disabled="approvalSteps.data.length === index + 1"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </Step>
  <Divider></Divider>
  <Step :step-number="3" title="Descripción del flujo de aprobación">
    <div class="flex flex-column md:w-5 h-5">
      <Textarea class="mt-2" style="min-width: 250px" v-model="description" />
      <small id="text-error" class="p-error">{{
        descriptionError || '&nbsp;'
      }}</small>
    </div>
  </Step>
  <Footer>
    <Button
      label="Cancelar"
      class="mr-2 text-sm font-light"
      severity="secondary"
      @click="
        router.push('/payrollsConfig/' + payrollConfigId + '/approvalFlows')
      "
    />
    <Button
      label="Guardar"
      class="font-light text-sm"
      severity="primary"
      @click="onSave"
      :disabled="!isValidToSave"
    />
  </Footer>
</template>
<script setup lang="ts">
  import { computed, onMounted, reactive, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import Footer from '@/shared/components/Footer.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import Step from '@/shared/components/Step.vue'
  import * as yup from 'yup'
  import MultiSelectDialog from '@/shared/components/MultiSelectDialog.vue'
  import { getApproversByCompanyId } from '../services/ApproverService'
  import { getPayrollsByConfigId } from '@/modules/payrolls-config/services/PayrollService'
  import { getEmplopeesByPayrollConfigId } from '@/modules/employees/services/EmployeeService'
  import ApprovalStep from '../models/ApprovalStep'
  import { storeToRefs } from 'pinia'
  import Checkbox from 'primevue/checkbox'
  import useCompany from '@/store/company'
  import { useField } from 'vee-validate'
  import useNotifications from '@/store/notification'
  import { createApprovalFlow } from '../services/ApprovalFlowService'
  import { createApprovalFlowSteps } from '../services/ApprovalFlowStepService'
  import {
    CreateApprovalFlowStepDto,
    ApprovalStepInputDto,
  } from '../services/interfaces/CreateApprovalFlowStepDto'
  import router from '@/router'
  import useLoading from '@/store/load'

  const path = useRoute()
  const payrollConfigId = ref()

  const entities = ref([
    { code: 'ALL', name: 'Toda las entidades' },
    { code: 'E', name: 'Empleados' },
    { code: 'N', name: 'Nóminas' },
  ])

  const companyStore = useCompany()
  const { company } = storeToRefs(companyStore)

  companyStore.$subscribe(async (_, state) => {
    company.value = state.company
    const approversResponse = await getApproversByCompanyId(
      company.value?.id || ''
    )
    approvers.value = approversResponse
  })

  //variables used in the first step
  const selectedEntity = ref(entities.value[0])
  const approvers = ref([
    {
      id: '',
      name: 'Supervisor del empleado',
    },
  ])

  const notificationStore = useNotifications()
  const loadingStore = useLoading()

  const employees = ref()
  const payrolls = ref()
  const dialogVisible = ref(false)
  const isDisabled = ref(false) // Set to true to disable the

  //variables used in the second step
  const approvalSteps = reactive({
    data: [
      {
        stepNumber: 0,
        weight: 0,
        description: '',
        useEmployeeSupervisor: false,
        approver: {
          id: '',
          name: '',
          userId: '',
        },
        isBackup: false,
      },
    ] as ApprovalStep[],
    totalItems: 1,
  })

  const descriptionSchema = yup
    .string()
    .required('La descripción del flujo es requerida')
  const employeesSchema = yup
    .array()
    .min(1, 'Al menos un empleado debe ser seleccionado')
    .required('los empleados son requeridos')
  const payrollsSchema = yup
    .array()
    .min(1, 'Al menos una nómina debe ser seleccionado')
    .required('Las nóminas son requeridas')
  const {
    value: description,
    errorMessage: descriptionError,
    validate: descValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: selectedEmployees,
    errorMessage: employeesError,
    validate: employeesValidate,
  } = useField<[]>('selectedEmployees', employeesSchema)
  const {
    value: selectedPayrolls,
    errorMessage: payrollsError,
    validate: payrollsValidate,
  } = useField<[]>('selectedPayrolls', payrollsSchema)
  const editingRows = ref([])

  onMounted(async () => {
    loadingStore.setLoadingState()
    payrollConfigId.value = path.params.id
    const employeesResponse = await getEmplopeesByPayrollConfigId(
      payrollConfigId.value
    )
    employees.value = employeesResponse.map((employee) => ({
      id: employee.id,
      name: employee.code + '-' + employee.name,
    }))

    const payrollReponse = await getPayrollsByConfigId(
      String(payrollConfigId.value)
    )
    payrolls.value = payrollReponse.map((payroll) => ({
      id: payroll.id,
      name: payroll.code + '-' + payroll.name,
    }))

    const approversResponse = await getApproversByCompanyId(
      company.value?.id || ''
    )
    approvers.value = approvers.value.concat(approversResponse)
    loadingStore.dismissLoadingState()
  })

  const updateDialogVisibility = (value: boolean) => {
    dialogVisible.value = value
  }

  const deleteApprovalStep = (
    event: any,
    id: number,
    approverId: string,
    approverName: string
  ) => {
    addApproverFromDeletedStep(approverId, approverName)
    approvalSteps.data = approvalSteps.data.filter(
      (approvalStep) => approvalStep.stepNumber !== id
    )
  }

  const onSave = async () => {
    loadingStore.setLoadingState()
    const isValid =
      (await descValidate()) &&
      (await employeesValidate()) &&
      (await payrollsValidate())

    if (approvalSteps.data.length === 1) {
      notificationStore.addErrorNotification({
        detail: 'Debe agregar al menos un paso de aprobación',
        severity: 'error',
        summary: 'Error',
      })
      return
    }

    const approvalFlowId = await createApprovalFlow({
      description: description.value,
      onlyEmployees: selectedEmployees.value ? selectedEmployees.value : [],
      onlyPayrolls: selectedPayrolls.value ? selectedPayrolls.value : [],
      payrollConfigId: payrollConfigId.value,
    })

    const inputSteps: ApprovalStepInputDto[] = []

    //Delete last step because is empty
    approvalSteps.data.pop()

    approvalSteps.data.forEach((step) => {
      inputSteps.push({
        approvalFlowId: approvalFlowId || '',
        approverId: step.approver?.id || '',
        description: step.description || '',
        isBackup: step.isBackup || false,
        useEmployeeSupervisor:
          step.approver?.id === '' ? true : step.useEmployeeSupervisor || false,
        weight: step.weight || 0,
      })
    })

    const approvers: CreateApprovalFlowStepDto = {
      steps: inputSteps,
    }

    const response = await createApprovalFlowSteps(approvers)
    loadingStore.dismissLoadingState()
    if (response === 'OK') {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Flujo de aprobación creado con exito`,
      })

      router.push('/payrollsConfig/' + payrollConfigId.value + '/approvalFlows')
    }
  }

  const isValidToSave = computed(() => {
    return (
      approvalSteps.data.length > 1 &&
      description.value &&
      (selectedEntity.value.code === 'ALL' ||
        (selectedEntity.value.code === 'E'
          ? selectedEmployees.value.length > 0
          : selectedPayrolls.value.length > 0))
    )
  })

  const onRowEditSave = (event: any) => {
    let { newData, data, index } = event
    approvalSteps.data[index] = newData

    if (data.approver.id && data.approver.name) {
      addApproverFromDeletedStep(data.approver.id, data.approver.name)
    } else {
      addApproverFromDeletedStep('', 'Supervisor del empleado')
    }

    //this is used when the approver is removed from the step
    removeApproverAssigned(newData.approver.id)

    if (
      newData.approver.name !== '' &&
      index === approvalSteps.data.length - 1
    ) {
      const newStepNumber = Number(approvalSteps.data[index].stepNumber) + 1
      approvalSteps.data.push({
        stepNumber: newStepNumber,
        useEmployeeSupervisor: false,
        weight: 0,
        description: '',
        approver: {
          id: '',
          name: '',
          userId: '',
        },
        isBackup: false,
      })
    }
  }

  const removeApproverAssigned = (id: string) => {
    approvers.value = approvers.value.filter((approver) => approver.id !== id)
  }

  const addApproverFromDeletedStep = (id: string, name: string) => {
    approvers.value.push({ id, name })
  }
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  .actions {
    display: flex;
    max-width: 180px;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    margin-right: 10px;
  }
</style>
